import { createSlice, current } from '@reduxjs/toolkit';
import {
  TARGET_COMMISSION,
  TARGET_SUB_AGENT_LIST,
  TARGET_SUB_SETTINGS,
} from '../helpers/variables';
const defaultState = {
  fetching: false,
  changingApplicationStatus: false,
  fetchingStagedPremium: false,
  fetchingCoverageTypes: false,
  fetchingStages: false,
  posting: false,
  patching: false,
  fetchedApplicationPremium: '',
  errorResponse: '',
  successMessage: '',
  notes: '',
  comments: '',
  endorsements: '',
  validFrom: '',
  validUntil: '',
  stagesSubSettings: {},
  agentCommissionsList: {},
  underwriterRatePercentage: '',
  selectedAgent: '',
  stages: [],
  validationMessage: '',
  caseTypeName: '',
  caseTypeId: '',
  subCaseTypeName: '',
  subCaseTypeId: '',
  workSourceName: '',
  solicitorGroupName: '',
  solicitorGroupId: '',
  coveragesOptions: [],
  deductiblesOptions: [],
  endorsementsOptions: [],
  selectedCoverageType: '',
  selectedDeductible: '',
  selectedEndorsement: '',
  coverageOptionName: '',
  deductibleOptionName: '',
  endorsementOptionName: '',
  applicationPremiumId: '',
};

const wizardActions = {
  resetStagedWizardState: () => {
    return defaultState;
  },
  resetApplicationStagedPremium: (state) => {
    if (state.fetchedApplicationPremium != '') {
      const appliedPremium = state.fetchedApplicationPremium;
      const underwriterRatePercent =
        appliedPremium?.applicationStagedPremiumSubSettingDtos[0]
          ?.underwriterRatePercentage;
      const subSettings =
        appliedPremium.applicationStagedPremiumSubSettingDtos?.reduce(
          (values, stage) => {
            values[stage.stageMachineName] = {
              id: stage.id.toString(),
              stageName: stage.stageName,
              stageMachineName: stage.stageMachineName,
              limitOfIndemnity: stage.limitOfIndemnity,
              premiumAmount: stage.premiumAmount,
              underwriterRate: stage.underwriterRate,
              underwriterRatePercentage: stage.underwriterRatePercentage,
              stagedPremiumSubSettingCommissionDtos:
                stage.stagedPremiumSubSettingCommissionDtos,
            };
            return values;
          },
          {}
        );
      const agentsList =
        appliedPremium?.applicationStagedPremiumSubSettingDtos[0].stagedPremiumSubSettingCommissionDtos?.reduce(
          (values, agent) => {
            values[agent.lawsysId] = {
              id: agent.lawsysId?.toString(),
              lawsysId: agent.lawsysId,
              name: agent.lawsysName,
              commissionPercentage: agent.commissionPercentage,
              commission: agent.commission,
            };
            return values;
          },
          {}
        );

      state.fetching = false;
      state.applicationPremiumId = appliedPremium.id;
      state.stagesSubSettings = subSettings;
      state.selectedCoverageType = appliedPremium?.coverageOptionId;
      state.selectedDeductible = appliedPremium?.deductibleOptionId;
      state.selectedEndorsement = appliedPremium?.endorsementOptionId;
      state.coverageOptionName = appliedPremium.coverageOptionName;
      state.deductibleOptionName = appliedPremium.deductibleOptionName;
      state.endorsementOptionName = appliedPremium.endorsementOptionName;
      state.notes = appliedPremium?.notes;
      state.comments = appliedPremium?.comments;
      state.validFrom = new Date(appliedPremium?.validFrom);
      state.validUntil = new Date(appliedPremium?.validUntil);
      state.agentCommissionsList = agentsList;
      state.underwriterRatePercentage = underwriterRatePercent;
    }
  },
  changeSubSettings: (state, action) => {
    const { target, key, value } = action.payload;
    const targetValues =
      key === TARGET_COMMISSION ? TARGET_SUB_AGENT_LIST : TARGET_SUB_SETTINGS;
    if (isNaN(value.toString()) === true || parseFloat(value.toString()) < 0)
      return;
    state[targetValues] = {
      ...state[targetValues],
      [target]: {
        ...state[targetValues][target],
        [key]: value,
      },
    };
  },
  changeWizardData: (state, { payload }) => {
    const { key, value } = payload;
    const currentState = current(state);
    if (
      key === 'underwriterRatePercentage' &&
      (isNaN(value.toString()) === true || parseFloat(value.toString()) < 0)
    )
      return;
    if (key === 'selectedCoverageType') {
      const coverageToDisplay = currentState.coveragesOptions.find(
        (o) => o.id.toString() === value.toString()
      );
      if (coverageToDisplay) {
        state.coverageOptionName = coverageToDisplay.name;
      }
    } else if (key === 'selectedDeductible') {
      const deductibleToDisplay = currentState.deductiblesOptions.find(
        (o) => o.id.toString() === value.toString()
      );
      if (deductibleToDisplay) {
        state.deductibleOptionName = deductibleToDisplay.name;
      }
    } else if (key === 'selectedEndorsement') {
      const endorsementToDisplay = currentState.endorsementsOptions.find(
        (o) => o.id.toString() === value.toString()
      );
      if (endorsementToDisplay) {
        state.endorsementOptionName = endorsementToDisplay.name;
      }
    }

    state[key] = value;
  },
  getCoverageTypes: (state) => {
    state.fetchingCoverageTypes = true;
  },
  failedGetCoverageTypes: (state) => {
    state.fetchingCoverageTypes = false;
  },
  receiveCoverageTypes: (state, action) => {
    state.fetchingCoverageTypes = false;
    state.coveragesOptions = action.payload.coverageOptions;
    state.deductiblesOptions = action.payload.deductibleOptions;
    state.endorsementsOptions = action.payload.endorsementOptions;
  },
  requestStagedPremiumSettingsStages: (state) => {
    state.fetchingStages = true;
  },
  failedStagedPremiumSettingsStages: (state) => {
    state.fetchingStages = false;
  },
  receiveStagedPremiumSettingsStages: (state, action) => {
    const subSettings = action.payload.map((c) => ({
      id: c.id.toString(),
      stageName: c.name,
      stageMachineName: c.machineName,
      limitOfIndemnity: '',
      premiumAmount: '',
      underwriterRate: '',
      underwriterRatePercentage: '',
      stagedPremiumSubSettingCommissionDtos: [],
    }));
    (state.fetchingStages = false),
      (state.staged = action.payload),
      (state.stagesSubSettings = subSettings.reduce((values, stage) => {
        values[stage.stageMachineName] = stage;
        return values;
      }, {}));
  },
  createStagedPremium: (state) => {
    state.posting = true;
    state.errorResponse = '';
  },
  patchStagedPremium: (state) => {
    state.patching = true;
    state.errorResponse = '';
  },
  failedCreateStagedPremium: (state, action) => {
    state.posting = false;
    try {
      state.errorResponse = action.payload.response.body.message;
    } catch (e) {
      state.errorResponse = 'An error occured';
    }
  },
  failedPatchStagedPremium: (state, action) => {
    state.patching = false;
    try {
      state.errorResponse = action.payload.response.body.message;
    } catch (e) {
      state.errorResponse = 'An error occured';
    }
  },

  successCreateStagedPremium: (state) => {
    state.posting = false;
    state.successMessage = 'Staged Premium Setting created successfully';
  },
  successPatchStagedPremium: (state) => {
    state.patching = false;
    state.successMessage = 'Staged Premium Setting updated successfully';
  },

  getSingleStagedPremium: (state) => {
    state.fetchingStagedPremium = true;
  },
  failedGetSingleStagedPremium: (state, action) => {
    state.fetchingStagedPremium = false;
    try {
      state.errorResponse = action.payload.response.body.message;
    } catch (e) {
      state.errorResponse = 'An error occured';
    }
  },
  receiveSingleStagedPremium: (state, action) => {
    const agentsCommissions =
      action.payload.stagedPremiumSubSettings[0].stagedPremiumSubSettingCommissionDtos.map(
        (c) => {
          return { ...c, id: c.lawsysId, name: c.lawsysName };
        }
      );
    const underwriterRatePercentageValue =
      action.payload.stagedPremiumSubSettings[0].underwriterRatePercentage;

    const coverageToDisplay = state.coveragesOptions.find(
      (o) => o.id === action.payload.coverageOptionId
    );
    const deductibleToDisplay = state.deductiblesOptions.find(
      (o) => o.id === action.payload.deductibleOptionId
    );
    const endorsementToDisplay = state.endorsementsOptions.find(
      (o) => o.id === action.payload.endorsementOptionId
    );
    state.fetchingStagedPremium = false;
    state.agentCommissionsList = agentsCommissions.reduce((values, agent) => {
      values[agent.lawsysId] = {
        id: agent.id.toString(),
        lawsysId: agent.lawsysId,
        name: agent.lawsysName,
        commissionPercentage: agent.commissionPercentage,
        commission: agent.commission,
      };
      return values;
    }, {});
    state.underwriterRatePercentage = underwriterRatePercentageValue;
    state.stagesSubSettings = action.payload?.stagedPremiumSubSettings.reduce(
      (values, stage) => {
        values[stage.stageMachineName] = {
          id: stage.id.toString(),
          stageName: stage.stageName,
          stageMachineName: stage.stageMachineName,
          limitOfIndemnity: stage.limitOfIndemnity.toString(),
          premiumAmount: stage.premiumAmount.toString(),
          underwriterRate: stage.underwriterRate.toString(),
          underwriterRatePercentage: stage.underwriterRatePercentage.toString(),
          stagedPremiumSubSettingCommissionDtos:
            stage.stagedPremiumSubSettingCommissionDtos,
        };
        return values;
      },
      {}
    );
    state.selectedCoverageType = action.payload?.coverageOptionId;
    state.selectedDeductible = action.payload?.deductibleOptionId;
    state.selectedEndorsement = action.payload?.endorsementOptionId;
    state.notes = action.payload?.notes;
    state.comments = action.payload?.comments;
    state.coverageOptionName = coverageToDisplay?.name;
    state.deductibleOptionName = deductibleToDisplay?.name;
    state.endorsementOptionName = endorsementToDisplay?.name;
    state.validFrom = new Date(action.payload?.validFrom);
    state.validUntil = action.payload?.validUntil ? new Date(action.payload?.validUntil):'';
    state.averageExpectedPremium = action.payload?.averageExpectedPremium;
  },
  getApplicationStagedPremiums: (state) => {
    state.fetchingStagedPremium = true;
  },
  failedGetApplicationStagedPremiums: (state, action) => {
    state.fetchingStagedPremium = false;
    try {
      state.errorResponse = action.payload.response.body.message;
    } catch (e) {
      state.errorResponse = 'An error occured';
    }
  },
  receiveWizardApplicationStagedPremiums: (state, action) => {
    if (
      action.payload.applicationPremiumSubSettingsDtos.length === 0 &&
      action.payload.applicationStagedPremiumSubSettingDtos.length === 0
    ) {
      state.fetching = false;
      state.errorResponse = 'Failed to load application premium settings';
    }
    if (
      action.payload?.applicationStagedPremiumSubSettingDtos &&
      action.payload?.applicationStagedPremiumSubSettingDtos?.length > 0
    ) {
      const underwriterRatePercent =
        action.payload?.applicationStagedPremiumSubSettingDtos[0]
          ?.underwriterRatePercentage;

      const subSettings =
        action.payload?.applicationStagedPremiumSubSettingDtos?.reduce(
          (values, stage) => {
            values[stage.stageMachineName] = {
              id: stage.id.toString(),
              stageName: stage.stageName,
              stageMachineName: stage.stageMachineName,
              limitOfIndemnity: stage.limitOfIndemnity,
              premiumAmount: stage.premiumAmount,
              underwriterRate: stage.underwriterRate,
              underwriterRatePercentage: stage.underwriterRatePercentage,
              stagedPremiumSubSettingCommissionDtos:
                stage.stagedPremiumSubSettingCommissionDtos,
            };
            return values;
          },
          {}
        );

      const agentsList =
        action.payload?.applicationStagedPremiumSubSettingDtos[0].stagedPremiumSubSettingCommissionDtos?.reduce(
          (values, agent) => {
            values[agent.lawsysId] = {
              id: agent.lawsysId?.toString(),
              lawsysId: agent.lawsysId,
              name: agent.lawsysName,
              commissionPercentage: agent.commissionPercentage,
              commission: agent.commission,
            };
            return values;
          },
          {}
        );

      const fetchedAppPremium = action.payload;
      state.errorResponse = '';
      state.fetchingStagedPremium = false;
      state.fetchedApplicationPremium = fetchedAppPremium;
      state.applicationPremiumId = action.payload.id;
      state.stagesSubSettings = subSettings;
      state.selectedCoverageType = action.payload?.coverageOptionId;
      state.selectedDeductible = action.payload?.deductibleOptionId;
      state.selectedEndorsement = action.payload?.endorsementOptionId;
      state.coverageOptionName = action.payload.coverageOptionName;
      state.deductibleOptionName = action.payload.deductibleOptionName;
      state.endorsementOptionName = action.payload.endorsementOptionName;
      state.averageExpectedPremium = action.payload.averageExpectedPremium;
      state.notes = action.payload?.notes;
      state.comments = action.payload?.comments;
      state.validFrom = new Date(action.payload?.validFrom);
      state.validUntil = new Date(action.payload?.validUntil);
      state.agentCommissionsList = agentsList;
      state.underwriterRatePercentage = underwriterRatePercent;
    }
  },
  requestPatchApplicationStagedPremiumSettings: (state, action) => {
    state.patching = true;
    if (action.payload.makeApplicationLive == true) {
      state.changingApplicationStatus = true;
    }
  },
  successPatchApplicationStagedPremiumSettings: (state, action) => {
    state.patching = false;
    if (action.payload.applicationStagedPremiumSubSettingDtos) {
      if (action.payload.applicationStagedPremiumSubSettingDtos.length > 0) {
        const underwriterRatePercent =
          action.payload?.applicationStagedPremiumSubSettingDtos[0]
            ?.underwriterRatePercentage;
        const agentCommissions =
          action.payload?.applicationStagedPremiumSubSettingDtos[0].stagedPremiumSubSettingCommissionDtos.map(
            (c) => ({ ...c, id: c.lawsysId, name: c.lawsysName })
          );
        state.applicationPremiumId = action.payload.id;
        state.stagesSubSettings =
          action.payload?.applicationStagedPremiumSubSettingDtos;
        state.selectedCoverageType = action.payload?.coverageOptionId;
        state.selectedDeductible = action.payload?.deductibleOptionId;
        state.selectedEndorsement = action.payload?.endorsementOptionId;
        state.coverageOptionName = action.payload.coverageOptionName;
        state.deductibleOptionName = action.payload.deductibleOptionName;
        state.endorsementOptionName = action.payload.endorsementOptionName;
        state.averageExpectedPremium = action.payload.averageExpectedPremium;
        state.notes = action.payload?.notes;
        state.comments = action.payload?.comments;
        state.validFrom = new Date(action.payload?.validFrom);
        state.validUntil = new Date(action.payload?.validUntil);
        state.agentCommissionsList = agentCommissions;
        state.underwriterRatePercentage = underwriterRatePercent;
      }
      if (action.payload?.changingApplicationStatus == false) {
        state.changingApplicationStatus = false;
      }
    }
  },
  failedPatchApplicationStagedPremiumSettings: (state, action) => {
    state.patching = false;
    state.changingApplicationStatus = false;
    try {
      state.errorResponse = action.payload.response.body.message;
    } catch (e) {
      state.errorResponse = 'An error occured';
    }
  },
};

const stagedPremiumsWizard = createSlice({
  name: 'stagedPremiumsWizard',
  initialState: defaultState,
  reducers: wizardActions,
});

export const {
  resetStagedWizardState,
  resetApplicationStagedPremium,
  changeSubSettings,
  requestStagedPremiumSettingsStages,
  failedStagedPremiumSettingsStages,
  receiveStagedPremiumSettingsStages,
  createStagedPremium,
  patchStagedPremium,
  failedCreateStagedPremium,
  failedPatchStagedPremium,
  successCreateStagedPremium,
  successPatchStagedPremium,
  getSingleStagedPremium,
  failedGetSingleStagedPremium,
  receiveSingleStagedPremium,
  changeWizardData,
  getCoverageTypes,
  failedGetCoverageTypes,
  receiveCoverageTypes,
  getApplicationStagedPremiums,
  failedGetApplicationStagedPremiums,
  receiveWizardApplicationStagedPremiums,
  requestPatchApplicationStagedPremiumSettings,
  successPatchApplicationStagedPremiumSettings,
  failedPatchApplicationStagedPremiumSettings,
} = stagedPremiumsWizard.actions;

export default stagedPremiumsWizard.reducer;
